import React, { forwardRef, useId } from "react";
import { Link, linkVariants } from "../../atoms/Link/index.js";
import { STATE_RING } from "../../lib/state-ring.js";
import { cn } from "../../lib/utils.js";

interface ListItemBaseProps extends Omit<
  React.LiHTMLAttributes<HTMLLIElement>,
  "onSelect"
> {
  /** Leading slot — icon, StatusDot, Avatar… */
  leading?: React.ReactNode;
  /** Trailing slot — Badge, value, chevron… On an interactive row it sits
   *  above the stretched target, so a control here stays clickable. */
  trailing?: React.ReactNode;
  /** A line under the content. On an interactive row it describes the row's
   *  link or button (aria-describedby) instead of joining its name. */
  description?: React.ReactNode;
  /** The location in a set, or the item shown in a master-detail pane: sets
   *  aria-current (`true` becomes "true") and paints the teal selection fill. */
  current?: boolean | "page" | undefined;
}

/** Static row, link row or button row: exclusive by type, so `selected`
 *  without `onSelect`, or `href` with `onSelect`, does not compile. */
type ListItemTarget =
  | {
      href?: undefined;
      onNavigate?: undefined;
      onSelect?: undefined;
      selected?: undefined;
    }
  | {
      /** Renders a Link that fills the row (stretched). */
      href: string;
      /** Absent: the browser follows `href`. Present: an unmodified primary
       *  click is handed here instead; middle-click and the modifier chords
       *  stay with the browser (Breadcrumb's rule). */
      onNavigate?: ((href: string) => void) | undefined;
      onSelect?: undefined;
      selected?: undefined;
    }
  | {
      /** Renders a type=button that fills the row. */
      onSelect: () => void;
      /** A chosen item (palette tiles, pickers): sets aria-pressed and paints
       *  the teal selection fill. Only with `onSelect`. */
      selected?: boolean | undefined;
      href?: undefined;
      onNavigate?: undefined;
    };

export type ListItemProps = ListItemBaseProps & ListItemTarget;

/* Teal marks the selected state only (C7): the same soft fill NavList and
   PopoverMenuItem paint for their current item. Any other interactive row
   takes NavList's neutral hover and pressed paint. High contrast adds the
   state ring, because the tint alone is too faint there (d15). */
const SELECTION = `bg-pb-teal-soft ${STATE_RING.teal}`;
const ROW_IDLE = "hover:pb-overlay-paint active:pb-overlay-paint-strong";

/* The button row reads Link's own stretched recipe, so there is one overlay
   recipe, not two.
   states-check-ignore: the focus treatment lives in the linkVariants recipe
   (atoms/Link), which both the link and the button row read. */
const BUTTON_TARGET = cn(
  linkVariants({ variant: "plain", display: "block", stretched: true }),
  "w-full text-start",
);

/* One row of a List. Without `href` or `onSelect` it is today's static <li>.
   With either, the row's target fills it through a stretched ::after overlay
   on a `relative` row; only `trailing` is lifted above that overlay. It takes
   no asChild (a row is not a single element): a router link is `href` plus
   `onNavigate`, the host navigating. */
export const ListItem = forwardRef<HTMLLIElement, ListItemProps>(
  function ListItem(
    {
      leading,
      trailing,
      description,
      current,
      href,
      onNavigate,
      onSelect,
      selected,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const descriptionId = useId();
    const interactive = href !== undefined || onSelect !== undefined;
    const ariaCurrent: "true" | "page" | undefined =
      current === true ? "true" : current || undefined;
    const target = {
      "aria-current": ariaCurrent,
      "aria-describedby": description != null ? descriptionId : undefined,
    };

    let content: React.ReactNode = children;
    if (href !== undefined) {
      content = (
        <Link
          href={href}
          variant="plain"
          display="block"
          stretched
          {...target}
          onClick={(event) => {
            if (!onNavigate || event.defaultPrevented) return;
            // A plain primary click is ours; middle-click and the modifier
            // chords (new tab, new window) belong to the browser. Enter on a
            // focused anchor arrives as a primary click.
            if (event.button !== 0) return;
            if (
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            )
              return;
            event.preventDefault();
            onNavigate(href);
          }}
        >
          {children}
        </Link>
      );
    } else if (onSelect !== undefined) {
      content = (
        <button
          type="button"
          className={BUTTON_TARGET}
          aria-pressed={selected}
          {...target}
          onClick={() => onSelect()}
        >
          {children}
        </button>
      );
    }

    return (
      <li
        ref={ref}
        className={cn(
          "flex items-center gap-3 text-sm text-pb-text",
          interactive && "relative",
          current || selected ? SELECTION : interactive && ROW_IDLE,
          className,
        )}
        aria-current={interactive ? undefined : ariaCurrent}
        {...rest}
      >
        {leading && (
          <span className="shrink-0 text-pb-text-muted">{leading}</span>
        )}
        <span className="min-w-0 flex-1">
          {content}
          {description != null && (
            <span
              id={descriptionId}
              className="block text-xs text-pb-text-muted"
            >
              {description}
            </span>
          )}
        </span>
        {trailing && (
          <span className={cn("shrink-0", interactive && "relative z-10")}>
            {trailing}
          </span>
        )}
      </li>
    );
  },
);
